import { t } from '@glory/i18n';
import { Button, Callout, CardPage, EmptyState, SkeletonRows } from '@glory/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PackageX } from 'lucide-react';

import { ItemCard } from '../../../features/catalog/item-card.tsx';
import { useCrumbs } from '../../../lib/crumbs.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/catalog/$id')({
  beforeLoad: requirePermissionRoute('catalog.read'),
  component: ItemPage,
});

/** Услуга на всю страницу (⌘↵ из панели): та же карточка, больше места для задач и описания. */
function ItemPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const seesCost = can('pricing.read');
  const item = useQuery(orpc.catalog.items.get.queryOptions({ input: { id } }));
  const categories = useQuery(orpc.catalog.categories.list.queryOptions());
  const specializations = useQuery(
    orpc.org.specializations.list.queryOptions({ input: { includeArchived: true } }),
  );
  const pricing = useQuery({ ...orpc.catalog.pricingContext.queryOptions(), enabled: seesCost });
  const back = () => {
    void navigate({ to: '/catalog', search: item.data ? { item: id } : {} });
  };
  useCrumbs(item.data ? [{ label: item.data.name }] : null);

  if (item.isPending) {
    return (
      <div className="px-4 py-5 md:px-6">
        <SkeletonRows rows={6} />
      </div>
    );
  }
  if (item.isError && item.error.message !== t('errors.common.not_found')) {
    return (
      <div className="px-4 py-5 md:px-6">
        <Callout>{item.error.message}</Callout>
      </div>
    );
  }
  if (!item.data) {
    return (
      <div className="px-4 py-10 md:px-6">
        <EmptyState
          icon={<PackageX />}
          title={t('catalog.notFound')}
          description={t('catalog.notFoundText')}
          action={<Button onClick={back}>{t('shell.backToList')}</Button>}
        />
      </div>
    );
  }

  return (
    <ItemCard
      key={item.data.id}
      item={item.data}
      categories={categories.data ?? []}
      specializations={specializations.data?.items ?? []}
      pricing={pricing.data ?? null}
      canManage={can('catalog.manage')}
      onDuplicated={(copy) => {
        void navigate({ to: '/catalog/$id', params: { id: copy } });
      }}
    >
      {(card) => (
        <CardPage
          title={card.title}
          meta={card.meta}
          tabs={card.tabs}
          actions={card.actions}
          onBack={back}
          backLabel={t('shell.navCatalog')}
        >
          {card.body}
        </CardPage>
      )}
    </ItemCard>
  );
}
